/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useCallback, useState } from 'react'
import { pickupSchema } from '../Pickup.schema'
import { useAddressLocation, useGeoCode, useZipCode } from 'containers/Address/Address.hooks'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

const getLocalityName = (options, key) =>
  options.filter((item) => item.value === key).reduce((acc, item) => acc + item.label, '')

export const usePickupForm = ({ pickup, addToast, onSuccess }) => {
  const [selectedCountry, setSelectedCountry] = useState(pickup?.countryId ? pickup.countryId : '')
  const [loading, setLoading] = useState(false)

  const { countries, states, cities, updateAvailableStates, updateAvailableCities, isLoadingStates, isLoadingCities } = useAddressLocation(
    addToast,
    pickup?.countryId,
    pickup?.stateId
  )

  const [zipCodeData, onZipCodeUpdate] = useZipCode(addToast)
  const [{ latLong }, onGeoCodeUpdate] = useGeoCode(addToast)

  const onZipCodeBlur = (value) => {
    if (value) {
      onZipCodeUpdate(value)
    }
  }

  const methods = useForm({
    resolver: yupResolver(pickupSchema(states, cities)),
    mode: 'onChange',
    defaultValues: {
      name: '',
      addressInfo: {
        country: '',
        state: '',
        city: '',
        zipCode: '',
        address: '',
        addressDetail: '',
        district: '',
        latLong: ''
      }
    }
  })

  const { setValue, getValues, handleSubmit } = methods
  const form = getValues()

  const handleSuccess = useCallback(
    async (formData) => {
      setLoading(true)

      try {
        await onSuccess(formData)
      } catch (error) {
        console.error('Error in handleSuccess:', error)
        addToast('Ocorreu um erro ao salvar os dados. Tente novamente.', { type: 'error' })
      } finally {
        setLoading(false)
      }
    },
    [onSuccess, methods, setSelectedCountry, addToast]
  )

  const onValidationError = useCallback(() => addToast('Preencha corretamente o formulário'), [addToast])
  const onSubmit = handleSubmit(handleSuccess, onValidationError)

  const getLatLng = useCallback(() => {
    const { zipCode = '', address = '', addressDetail = '', district = '' } = form?.addressInfo || {}

    const countryName = getLocalityName(countries, form?.addressInfo?.country ?? pickup?.countryId)
    const stateName = getLocalityName(states, form?.addressInfo?.state ?? pickup?.stateId)
    const cityName = getLocalityName(cities, form?.addressInfo?.city ?? pickup?.cityId)
    const addressParam = `${zipCode} ${address} ${addressDetail} ${district} ${countryName} ${stateName} ${cityName}`

    onGeoCodeUpdate(addressParam)
  }, [form, countries, states, cities, onGeoCodeUpdate, pickup])

  const onCountryChange = useCallback(
    async (countryValue) => {
      const country = countries.find((country) => Object.values(country).includes(countryValue))

      if (country) {
        setValue('addressInfo.country', country.value)
        setSelectedCountry(country.value)

        await updateAvailableStates(country.value)
      } else {
        updateAvailableStates(null)
        setSelectedCountry('')
        setValue('addressInfo.country', '')
        setValue('addressInfo.state', '')
        setValue('addressInfo.city', '')
      }
    },
    [countries, updateAvailableStates, setValue]
  )

  const onStateChange = useCallback(
    async (stateValue) => {
      const state = states.find((state) => Object.values(state).includes(stateValue))

      if (state) {
        setValue('addressInfo.state', state.value)
        await updateAvailableCities(selectedCountry, state.value)
      } else {
        updateAvailableCities(null)
        setValue('addressInfo.state', '')
        setValue('addressInfo.city', '')
      }
    },
    [states, updateAvailableCities, selectedCountry, setValue]
  )

  const updateAddress = useCallback(async (zipcode) => {
    if (!zipcode || Object.keys(zipcode).length === 0) {
      return
    }
    if (zipcode) setValue('addressInfo.country', 'BR')
    if (zipcode?.district) setValue('addressInfo.district', zipcode.district)
    if (zipcode?.address) setValue('addressInfo.address', zipcode.address)

    await onCountryChange('BR')
  }, [setValue, onCountryChange])

  useEffect(() => {
    if (!states || states.length === 0 || !zipCodeData?.state) return

    const state = states.find((state) => Object.values(state).includes(zipCodeData.state))

    if (state) {
      setValue('addressInfo.state', state.value)

      updateAvailableCities(selectedCountry, state.value)
    }
  }, [states, zipCodeData, setValue, updateAvailableCities, selectedCountry])

  useEffect(() => {
    if (!cities || cities.length === 0 || !zipCodeData?.city) return

    const city = cities.find((state) => Object.values(state).includes(zipCodeData.city))

    if (city) {
      setValue('addressInfo.city', city.value)
    }
  }, [zipCodeData, setValue, cities])

  const updateAddressLatLong = useCallback(() => {
    if (latLong) {
      setValue('addressInfo.latLong', latLong)
    }
  }, [latLong, setValue])

  useEffect(updateAddressLatLong, [latLong])
  useEffect(() => {
    updateAddress(zipCodeData)
  }, [zipCodeData])

  useEffect(() => {
    if (pickup) {
      methods.reset({
        name: pickup.name,
        companyId: pickup.companyId,
        id: pickup.id,
        addressInfo: {
          country: pickup?.countryId || undefined,
          state: pickup?.stateId || undefined,
          city: pickup?.cityId || undefined,
          zipCode: pickup?.postCode || undefined,
          address: pickup?.street || undefined,
          addressDetail: pickup?.supplement || undefined,
          district: pickup?.district || undefined,
          latLong: pickup?.latitude && pickup?.longitude ? `${pickup.latitude},${pickup.longitude}` : undefined
        }
      })
    }
  }, [pickup])

  return {
    methods,
    form,
    countries,
    states,
    cities,
    loading,
    isLoadingStates,
    isLoadingCities,
    onCountryChange,
    onStateChange,
    onSubmit,
    onZipCodeBlur,
    fetchCoordinates: getLatLng
  }
}
